import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { sx } from "../../design/ui";
/*
 * 圖片網址的白名單放在 design/domain.js：這支元件是「畫得出來嗎」的最終判準，
 * 而 domain 的 imagesOf（時間軸縮圖）要數的正是「畫得出來的圖有幾張」。
 * 兩邊各留一份遲早會分岔 —— 分岔的下場就是畫面寫「貼了 1 張圖，點圖看原尺寸」，
 * 點下去卻是一段「圖片不能顯示」的靜態文字。
 */
import { safeImgSrc } from "../../design/domain";

/* ===================== 消毒（白名單） =====================
 *
 * 為什麼一定要有這一段：這支元件最後是 dangerouslySetInnerHTML，而內容的來源
 * **不可信**。RichEditor 的貼上只把 data:image 換成上傳網址（replaceDataUris），
 * 標籤與屬性原樣放行；更關鍵的是 /MyTask/Progress 是一般 API，任何人都能直接
 * POST 任意 HTML，根本不必經過編輯器。
 *
 * innerHTML 插入的 <script> 不會執行，但 <img src=x onerror=…>、onload、onclick、
 * <iframe>、<a href="javascript:"> 全部會 —— 而看這些內容的人，正是有權限按
 * 「審核通過」的人。白名單寫在 RichView 裡面（而不是每個呼叫端）：改一個地方，
 * 需求說明、做法、會議紀錄、回報內容、審核頁一起受惠。
 *
 * 作法是「拆外殼、不丟內容」：不在白名單裡的排版標籤（<font>、<table>…）只把外殼
 * 拆掉、保留文字，因為使用者要的是內容；真正危險的那幾個（script/iframe/svg…）
 * 才連子樹一起刪。屬性一律砍光再把允許的放回去 —— 用黑名單擋 on* 遲早會漏。
 */

/** 留得下來的標籤：RichEditor（document.execCommand）產得出來的那些，加上圖與連結 */
const ALLOW_TAGS = new Set([
  "P", "BR", "DIV", "SPAN", "STRONG", "B", "EM", "I", "U", "S", "STRIKE",
  "SMALL", "SUB", "SUP", "MARK",
  "UL", "OL", "LI", "DL", "DT", "DD", "BLOCKQUOTE", "IMG", "A", "CODE", "PRE", "HR",
  "H1", "H2", "H3", "H4", "H5", "H6",
  // 表格留著（屬性反正會被砍光）：會議紀錄常常是整張表貼進來的，
  // 拆掉外殼會把一整張表壓成一串沒有分隔的字，比看不到還糟
  "TABLE", "THEAD", "TBODY", "TFOOT", "TR", "TD", "TH", "CAPTION", "COLGROUP", "COL",
]);

/**
 * 連子樹一起刪的標籤：留下來沒有任何內容價值，只有風險。
 *
 * 這裡只放「自己不帶使用者要看的內容」的那些：script/style/iframe 的子樹是程式碼或
 * 外部資源，<input> 是空元素（沒有子節點可以丟）。
 *
 * 表單相關的 <form>/<button>/<select>/<option>/<textarea> **刻意不放這裡** ——
 * 它們會包住真正的內容。從內部網頁框選一段複製貼進回報時，Chrome 的剪貼簿 HTML
 * 常常把祖先 <form> 一起帶進來，變成 <form><p>做好了</p><img src="/InlineImage/…"></form>；
 * 連子樹一起刪等於把整則回報（文字＋截圖）消滅，畫面只剩一塊空白 —— 正是這次要修的
 * 「看起來像壞掉」。它們不在 ALLOW_TAGS 裡，所以會走下面「拆外殼、留文字」那條路：
 * 外殼（能送出的 <form>、能按的 <button>）沒了，內容還在。
 */
const DROP_TAGS = new Set([
  "SCRIPT", "STYLE", "IFRAME", "FRAME", "FRAMESET", "OBJECT", "EMBED", "APPLET",
  "SVG", "MATH", "LINK", "META", "BASE", "TITLE", "INPUT",
  "VIDEO", "AUDIO", "SOURCE", "TRACK",
  "NOSCRIPT", "TEMPLATE",
]);

/**
 * style 只留這幾個字級以外的「純外觀」屬性。
 * 不整個丟掉是因為 execCommand 的顏色／置中就寫在 style 裡，丟了排版會走樣；
 * 但 position/z-index 之類的一定要擋掉 —— 那是拿別人的內容蓋住「通過」鈕的手法。
 */
const ALLOW_STYLE = [
  "color",
  "background-color",
  "font-weight",
  "font-style",
  "text-decoration",
  "text-align",
];

/** 連結只許 http(s) 與 mailto —— javascript: / data: 都是可以直接執行的 */
function safeHref(raw) {
  const v = String(raw || "").trim();
  if (!v) return "";
  if (/^\/(?!\/)/.test(v)) return v;
  if (/^(https?:\/\/|mailto:)/i.test(v)) return v;
  return "";
}

/** 從 CSSOM 取允許的宣告。用 el.style 而不是自己剖字串：瀏覽器已經幫忙擋掉不合法的值 */
function pickStyle(el) {
  const out = [];
  ALLOW_STYLE.forEach((k) => {
    let v = "";
    try {
      v = el.style.getPropertyValue(k);
    } catch (e) {
      v = "";
    }
    // url(...) 一律不要：背景圖同樣會對外發請求，也是追蹤像素的一條路
    if (v && !/url\s*\(/i.test(v) && !/expression\s*\(/i.test(v)) out.push(`${k}:${v}`);
  });
  return out.join(";");
}

function cleanAttrs(el) {
  const tag = el.tagName;
  // 先把要留的值讀出來，才能把屬性整批砍光
  const keepStyle = pickStyle(el);
  const src = tag === "IMG" ? safeImgSrc(el.getAttribute("src")) : "";
  const href = tag === "A" ? safeHref(el.getAttribute("href")) : "";
  const alt = el.getAttribute("alt") || "";
  // 表格只留合併格數（純數字，沒有攻擊面）；留著結構才不會走樣
  const span =
    tag === "TD" || tag === "TH"
      ? {
          colspan: String(el.getAttribute("colspan") || "").replace(/\D/g, ""),
          rowspan: String(el.getAttribute("rowspan") || "").replace(/\D/g, ""),
        }
      : null;

  if (tag === "IMG" && !src) {
    /*
     * 網址過不了白名單（javascript:、data:text/html、//外站 這種）的圖。
     * 不能直接 remove：那一列會變成一塊空白，跟「這則回報本來就沒東西」長得一樣 ——
     * 而「看不出來是圖沒載到」正是這次要修的問題。換成一段說得出原因的文字。
     */
    const tip = el.ownerDocument.createElement("span");
    tip.className = "rt-view__imgfail";
    tip.textContent = alt
      ? `無法顯示圖片：${alt}（圖片來源不受信任）`
      : "無法顯示圖片（圖片來源不受信任）";
    el.replaceWith(tip);
    return;
  }

  Array.from(el.attributes).forEach((a) => el.removeAttribute(a.name));
  if (keepStyle) el.setAttribute("style", keepStyle);
  if (span) {
    if (span.colspan) el.setAttribute("colspan", span.colspan);
    if (span.rowspan) el.setAttribute("rowspan", span.rowspan);
  }

  if (tag === "IMG") {
    el.setAttribute("src", src);
    // alt 一定要有：圖載不到時瀏覽器只剩 alt 可以講，空的就真的只剩一塊空白
    el.setAttribute("alt", alt || "圖片");
    // 一則回報可能貼十幾張截圖，收合區展開才載得起
    el.setAttribute("loading", "lazy");
    el.setAttribute("decoding", "async");
    // 從別的網站整段複製過來的 <img> 會原樣存下，每次檢視都對外發請求。
    // 擋不掉請求（那會讓舊資料的圖全壞），但至少不要把我們的網址送出去
    el.setAttribute("referrerpolicy", "no-referrer");
  }
  if (tag === "A") {
    if (href) {
      el.setAttribute("href", href);
      el.setAttribute("target", "_blank");
      el.setAttribute("rel", "noopener noreferrer nofollow");
    }
    // 沒有合格網址就留一個沒有 href 的 <a>：文字還在，只是點不動
  }
}

function sanitizeHtml(html) {
  const s = String(html || "");
  if (!s) return "";
  let doc = null;
  try {
    // DOMParser 產生的是 inert document：解析階段不載圖、不跑 script，消毒本身才安全
    doc = new DOMParser().parseFromString(s, "text/html");
  } catch (e) {
    doc = null;
  }
  // 解析不出來寧可整段不顯示，也不要把沒消毒過的原始 HTML 交給 innerHTML
  if (!doc || !doc.body) return "";

  const walk = (node) => {
    Array.from(node.children).forEach((el) => {
      const tag = el.tagName;
      if (DROP_TAGS.has(tag)) {
        el.remove();
        return;
      }
      walk(el);
      if (!ALLOW_TAGS.has(tag)) {
        // 拆外殼、留文字（<font>、<table> 這種）
        el.replaceWith(...Array.from(el.childNodes));
        return;
      }
      cleanAttrs(el);
    });
  };
  walk(doc.body);
  return doc.body.innerHTML;
}

/**
 * 富文本的唯讀呈現 + 圖片點擊放大。
 *
 * 需求說明裡貼的多半是螢幕截圖，而版面寬度只有幾百 px —— 縮成那樣根本看不清
 * 對話框裡寫什麼。所以點圖就用整頁遮罩開原尺寸，再點一下（或按 Esc）關掉。
 *
 * 事件掛在容器上而不是每個 <img>：內容是 dangerouslySetInnerHTML 來的，
 * 沒有 React 元素可以掛 onClick，冒泡到容器再看 target 是不是圖片最單純，
 * 內容換了也不必重新綁。
 */
function RichView({ html, sxStyle, className }) {
  /*
   * zoom = 目前放大顯示的那張圖（{ src, alt }），不是字級。
   * 字級走 utils/textsize.js 的 data-textsize，全站已經把 CSS zoom 拔光；
   * 這個同名的 state 是圖片燈箱，拔掉就等於拿掉「點圖看原尺寸」這個功能。
   */
  const [zoom, setZoom] = useState(null); // { src, alt }
  /** 放大的那張也可能載不到（檔案被刪、沒權限）—— 不能只給一片黑底 */
  const [zoomFailed, setZoomFailed] = useState(false);
  const boxRef = useRef(null);

  const safeHtml = useMemo(() => sanitizeHtml(html), [html]);

  /*
   * 本來有內容、消毒完卻一個字都不剩（整段只有 <script>／<style>，或 HTML 壞到
   * DOMParser 解析不出來）。這種時候畫面上會是一塊空白，而空白會被讀成「他根本沒寫」——
   * 對審核者來說這是會影響決定的誤會。寧可寫一句說得出原因的話。
   */
  const wiped = useMemo(() => !safeHtml && !!String(html || "").trim(), [html, safeHtml]);

  const close = useCallback(() => {
    setZoom(null);
    setZoomFailed(false);
  }, []);

  useEffect(() => {
    if (!zoom) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    // 放大時鎖住底層捲動，否則滾滑鼠會捲到後面的內容、關掉之後位置整個跑掉
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [zoom, close]);

  /*
   * 圖片載不到（檔案被刪、沒有權限、外站網址掛了）要**看得出來是圖沒載到**。
   * 預設行為在不同瀏覽器之間差很多：有的只留一塊空白，有的縮成一個小破圖示 ——
   * 兩種都會被讀成「這則回報本來就沒有東西」，正是這次要修的誤會。
   *
   * 不能靠內容自帶 onerror（消毒時就把 on* 砍光了，那是刻意的），也不能靠冒泡：
   * error 事件不冒泡，只有捕獲階段接得到。接到就把 <img> 換成一段說明文字。
   * 這是直接改 DOM，但這塊是 dangerouslySetInnerHTML 的地盤，React 不會來對帳；
   * html 換了 React 會整段重寫，這個 effect 也跟著重跑。
   */
  useEffect(() => {
    const root = boxRef.current;
    if (!root) return undefined;
    const onError = (e) => {
      const el = e.target;
      if (!el || el.tagName !== "IMG") return;
      const tip = document.createElement("span");
      tip.className = "rt-view__imgfail";
      const alt = el.getAttribute("alt") || "";
      tip.textContent =
        alt && alt !== "圖片"
          ? `無法載入圖片：${alt}（圖片可能已刪除，或您沒有檢視權限）`
          : "無法載入圖片：圖片可能已刪除，或您沒有檢視權限";
      el.replaceWith(tip);
    };
    root.addEventListener("error", onError, true);
    return () => root.removeEventListener("error", onError, true);
  }, [safeHtml]);

  const onClick = (e) => {
    const el = e.target;
    if (!el || el.tagName !== "IMG" || !el.getAttribute("src")) return;
    e.preventDefault();
    setZoomFailed(false);
    setZoom({ src: el.getAttribute("src"), alt: el.getAttribute("alt") || "圖片" });
  };

  return (
    <>
      <div
        ref={boxRef}
        className={`rt-view${className ? ` ${className}` : ""}`}
        style={sx(sxStyle)}
        onClick={onClick}
        role="presentation"
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />

      {/* 消毒後整段空掉時的說明。不能放進上面那個 div：dangerouslySetInnerHTML 不收 children */}
      {wiped && (
        <p className="rt-view__imgfail">
          此內容無法顯示（不含可安全顯示的內容，已被系統阻擋）。原始內容仍保留，請洽詢撰寫者。
        </p>
      )}

      {zoom && (
        /*
          用 Backdrop 而不是 Dialog：Esc 與鎖住底層捲動上面的 effect 已經自己做了，
          Dialog 也會去動 body 的 overflow，兩邊各自還原會互相蓋掉。
          z-index 要蓋在 TaskDrawer / 對話框之上；游標提示「再點一下就縮回去」。
        */
        <Backdrop
          open
          role="presentation"
          onClick={close}
          sx={{
            zIndex: 9000,
            cursor: "zoom-out",
            flexDirection: "column",
            gap: 2,
            p: 2,
            bgcolor: "rgba(0, 0, 0, 0.82)",
          }}
        >
          {/*
            圖片本身也吃 close：整片都能點掉最不會卡住使用者。
            要「點圖不關」得多一顆關閉鈕才說得通，這裡不值得。
          */}
          {zoomFailed ? (
            <Typography sx={{ color: "common.white", textAlign: "center" }}>
              無法載入圖片（{zoom.alt}）：圖片可能已刪除，或您沒有檢視權限。
            </Typography>
          ) : (
            <Box
              component="img"
              src={zoom.src}
              alt={zoom.alt}
              onError={() => setZoomFailed(true)}
              sx={{ maxWidth: "95vw", maxHeight: "82vh", objectFit: "contain", borderRadius: "var(--radius-md)" }}
            />
          )}
          {/*
            關閉方式寫成一顆帶文字的按鈕，不再是角落一行淺灰小字 ——
            「任何可點物件 ≥ 44px 且一定有文字」。點哪裡都能關，這顆只是把出口講明白。
          */}
          <Button variant="contained" onClick={close}>
            關閉（或按 Esc）
          </Button>
        </Backdrop>
      )}
    </>
  );
}

RichView.propTypes = {
  html: PropTypes.string,
  /** 內容容器的樣式字串（沿用 design/ui 的 sx 語法）。刻意不叫 style：
   *  那個名字會被 react/style-prop-object 當成 DOM 的 style 物件而報錯 */
  sxStyle: PropTypes.string,
  className: PropTypes.string,
};

RichView.defaultProps = { html: "", sxStyle: "", className: "" };

export default RichView;
